import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export type TabItem<T extends string> = {
  key: T;
  label: React.ReactNode;
};

type TabsBarProps<T extends string> = {
  tabs: TabItem<T>[];
  value: T;
  onChange: (key: T) => void;
  variant?: 'pill' | 'underline';
  className?: string;
  trackClassName?: string;
};

type Indicator = { left: number; width: number; ready: boolean };

export function TabsBar<T extends string>({
  tabs,
  value,
  onChange,
  variant = 'pill',
  className = '',
  trackClassName = '',
}: TabsBarProps<T>) {
  const trackRef = useRef<HTMLDivElement>(null);
  const buttonsRef = useRef(new Map<T, HTMLButtonElement>());
  const [indicator, setIndicator] = useState<Indicator>({ left: 0, width: 0, ready: false });

  const measure = useCallback(() => {
    const btn = buttonsRef.current.get(value);
    if (!btn) return;
    setIndicator({ left: btn.offsetLeft, width: btn.offsetWidth, ready: true });
  }, [value]);

  useLayoutEffect(() => {
    measure();
  }, [measure, tabs]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(track);
    return () => ro.disconnect();
  }, [measure]);

  // Quand la bande déborde (mobile), ramener l'onglet actif dans le champ de
  // vision. On bouge le scroll de la piste elle-même — scrollIntoView()
  // remonterait aussi les ancêtres et ferait sauter la page. Uniquement sur
  // changement d'onglet : à chaque rendu, ça contrarierait le scroll manuel.
  useEffect(() => {
    const track = trackRef.current;
    const btn = buttonsRef.current.get(value);
    if (!track || !btn) return;
    if (track.scrollWidth <= track.clientWidth) return; // rien à faire (ou piste masquée)
    const pad = 12;
    const left = btn.offsetLeft;
    const right = left + btn.offsetWidth;
    let next: number | null = null;
    if (left - pad < track.scrollLeft) next = Math.max(0, left - pad);
    else if (right + pad > track.scrollLeft + track.clientWidth)
      next = right + pad - track.clientWidth;
    if (next === null || next === track.scrollLeft) return;
    if (typeof track.scrollTo === 'function') {
      track.scrollTo({ left: next, behavior: 'smooth' });
    } else {
      track.scrollLeft = next;
    }
  }, [value]);

  const isPill = variant === 'pill';
  const activeColor = 'var(--pro-primary)';
  const idleColor = 'var(--pro-text-grey)';

  // Single row that scrolls horizontally on narrow screens (max-w-full caps it
  // to the parent, overflow-x-auto scrolls the rest) instead of wrapping to
  // multiple rows — wrapping also broke the sliding indicator (X-only).
  const baseTrack = isPill
    ? 'relative inline-flex items-center gap-1 max-w-full overflow-x-auto pro-noscrollbar'
    : 'relative inline-flex items-center gap-1 max-w-full overflow-x-auto pro-noscrollbar';

  const baseButton = isPill
    ? 'pro-tab shrink-0 h-9 px-3.5 rounded-full text-[13px] font-medium'
    : 'pro-tab shrink-0 px-4 py-3 text-[13px] font-medium';

  return (
    <div
      ref={trackRef}
      className={`${baseTrack} ${trackClassName} ${className}`.trim()}
    >
      {indicator.ready && (
        <span
          aria-hidden
          className={isPill ? 'pro-tabs-pill' : 'pro-tabs-underline'}
          style={{
            transform: `translateX(${indicator.left}px)`,
            width: indicator.width,
          }}
        />
      )}
      {tabs.map((t) => {
        const active = value === t.key;
        return (
          <button
            key={t.key}
            ref={(el) => {
              if (el) buttonsRef.current.set(t.key, el);
              else buttonsRef.current.delete(t.key);
            }}
            type="button"
            data-active={active ? 'true' : 'false'}
            onClick={() => onChange(t.key)}
            className={baseButton}
            style={{ color: active ? activeColor : idleColor }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
